import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { AssigneeProps, Subtask, Task } from 'types'
import isEqual from 'lodash/isEqual'
import { useUpdateTaskItems } from 'api/aws/tasks/useUpdateTaskItems'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useEntitiesFromSearch } from 'api/search'

interface OldSubtaskDataProps {
  subtasks: Subtask[]
  count: number
}

export const useTaskDrawerHandleFunctions = () => {
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const { mutate: updateTaskItem } = useUpdateTaskItems()
  const [isStaleData, setIsStaleData] = useState<boolean>(false)
  const { syncTasksCacheAfterBatchUpdate } = useSyncQueryCache()
  const { allTasks } = useEntitiesFromSearch()

  const handleSaveAssignees = ({ localAssignees, task }) => {
    console.log('localAssignees', localAssignees, 'task', task)
    // Only update if there is a change
    if (!isEqual(localAssignees, task?.assignees || [])) {
      const updatePayload = {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: task?.PK,
        skValue: task?.SK,
        fieldName: 'assignees',
        newValue: localAssignees,
      }
      console.log('Saving updatePayload:', updatePayload)

      updateTaskItem(
        { updates: [updatePayload] },
        {
          onSuccess: ({ updates }) => {
            const findTask = allTasks?.find(t => t?.SK === updates?.[0]?.skValue)
            const updatedAttributes = updates?.[0]?.updatedAttributes
            const updatedTask = { ...findTask, ...updatedAttributes }
            syncTasksCacheAfterBatchUpdate(queryClient, [updatedTask])
            queryClient.invalidateQueries({ queryKey: ['task', task?.PK, task?.SK] })
            showSnackbar('Assignees updated successfully', 'success')
            setIsStaleData(true)
          },
        }
      )
    }
  }
  const handleUpdateMasterStatus = (newStatus: string, task: Task) => {
    const updatePayload = {
      primaryKey: 'PK',
      sortKey: 'SK',
      tableName: 'TASKS_TABLE',
      pkValue: task?.PK,
      skValue: task?.SK,
      fieldName: 'masterStatus',
      newValue: newStatus,
    }
    const updatedTask = { ...task, masterStatus: newStatus }
    updateTaskItem(
      { updates: [updatePayload] },
      {
        onSuccess: () => {
          syncTasksCacheAfterBatchUpdate(queryClient, [updatedTask])
          queryClient.invalidateQueries({ queryKey: ['task', task?.PK, task?.SK] })
          showSnackbar('Master status updated successfully', 'success')
          setIsStaleData(true)
        },
      }
    )
  }
  const handleUpdateSubtaskStatus = (subtask: Subtask, newStatus: string, task: Task) => {
    const updatePayload = {
      primaryKey: 'PK',
      sortKey: 'SK',
      tableName: 'TASKS_TABLE',
      pkValue: subtask?.PK,
      skValue: subtask?.SK,
      fieldName: 'masterStatus',
      newValue: newStatus,
    }

    updateTaskItem(
      { updates: [updatePayload] },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['subtasks', task?.SK] })
          // patchSubtaskInTeamTasksCache(subtask, 'masterStatus', newStatus)
          // queryClient.invalidateQueries({ queryKey: ['teamTasks', {}] })

          showSnackbar('Subtask status updated successfully', 'success')
          setIsStaleData(true)
        },
      }
    )
  }
  const handleUpdateActiveAssignee = (subtask: Subtask, newAssignee: AssigneeProps, task: Task) => {
    const updatePayload = {
      primaryKey: 'PK',
      sortKey: 'SK',
      tableName: 'TASKS_TABLE',
      pkValue: subtask?.PK,
      skValue: subtask?.SK,
      fieldName: 'queue',
      newValue: newAssignee,
    }
    // if (changedFields?.queue) {
    //   notify({
    //     users: [changedFields?.queue],
    //     type: 'Update',
    //     severity: 'success',
    //     title: 'Added to Queue',
    //     message: `You've been added to queue for ${task?.name}`,
    //     board: 'teamtasks',
    //     boardItemName: task?.name,
    //     boardId: task?.SK,
    //     slackHeaderMessage: `You've been added to queue for ${task?.name}`,
    //     slackMessageHtml: `You've been added to queue for ${task?.name}`,
    //     sendSlack: true,
    //     sendApp: true,
    //     slackDirectLink: `/teamtasks/${task?.PK}/${task?.SK}/subtasks`,
    //   })
    // }
    updateTaskItem(
      { updates: [updatePayload] },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['subtasks', task?.SK] }) // ✅ For subtask details
          queryClient.invalidateQueries({ queryKey: ['task', task?.PK, task?.SK] }) // ✅ For parent task progress / UI
          // patchSubtaskInTeamTasksCache(subtask, 'queue', newAssignee) // ✅ For grid tree rows
          // queryClient.invalidateQueries({ queryKey: ['teamTasks', {}] })
          showSnackbar('Active assignee updated successfully', 'success')
          setIsStaleData(true)
        },
      }
    )
  }
  const handleSaveTaskNotes = (localAddtlNotes: string, task: Task) => {
    console.log('localAddtlNotes', localAddtlNotes, 'task', task)
    // Only update if there is a change
    if ((localAddtlNotes || '') !== (task?.addtlNotes || '')) {
      const updatePayload = {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: task?.PK,
        skValue: task?.SK,
        fieldName: 'addtlNotes',
        newValue: localAddtlNotes,
      }
      console.log('Saving updatePayload:', updatePayload)
      updateTaskItem(
        { updates: [updatePayload] },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['task', task?.PK, task?.SK] }) // ✅ For parent task progress / UI
            showSnackbar("Addt'l notes updated successfully", 'success')
            setIsStaleData(true)
          },
        }
      )
    }
  }
  const handleUpdateSubtaskDateRange = (subtask: Subtask, newStart: string, newEnd: string, task: Task) => {
    if (!subtask?.PK || !subtask?.SK || !task?.SK) return

    const updates = [
      {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: subtask.PK,
        skValue: subtask.SK,
        fieldName: 'startDate',
        newValue: newStart,
      },
      {
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        pkValue: subtask.PK,
        skValue: subtask.SK,
        fieldName: 'endDate',
        newValue: newEnd,
      },
    ]

    updateTaskItem(
      { updates },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['subtasks', task.SK] })
          // queryClient.invalidateQueries({ queryKey: ['teamTasks', {}] })
          showSnackbar('Subtask dates updated successfully', 'success')
          setIsStaleData(true)
        },
      }
    )
  }

  return {
    handleSaveAssignees,
    handleUpdateMasterStatus,
    handleUpdateSubtaskStatus,
    handleUpdateActiveAssignee,
    handleSaveTaskNotes,
    handleUpdateSubtaskDateRange,
    isStaleData,
    setIsStaleData,
  }
}
